import { el, clear } from "../components/dom.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { offerUndoDelete } from "../components/undo.js";
import { peopleRow } from "../components/people-row.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	OWNERSHIP_STATUSES,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getTvSeriesById,
	updateTvSeries,
	deleteTvSeries,
	setSeriesRating,
	setSeriesAwards,
	setSeriesNote,
	setSeasonSeen,
	getCatalog,
	saveCatalog,
	effectiveRating,
	effectiveSeen,
	NOTE_MAX_LENGTH,
} from "../db/tv.js";
import { getTvEpisodes } from "../api/tv.js";
import { groupsSection } from "../components/groups-section.js";
import { getGroupsContainingItem } from "../db/groups.js";
import { formatDate, formatReleaseDate, todayDate } from "../utils/dates.js";
import { seasonReleased, seasonPremiereDate, isCatalogStale } from "../utils/seasons.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import { magSectionHeader, magEpisodeRow, magLoading, MAG } from "../components/magazine/kit.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
} from "../components/magazine/detail.js";

export async function renderTvDetail(root, id) {
	const series = await getTvSeriesById(id);
	if (!series) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Series not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/tv") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	const cached = await getCatalog(series.tvmaze_id);
	mount(view, series, cached);

	// Lazy-load the season/episode catalog (cached locally, re-fetchable), and
	// re-fetch a STALE one: older than CATALOG_MAX_AGE_DAYS (premiere dates shift,
	// new seasons get announced) or predating the `premiere_date` field. Silent —
	// when the data changed, the view re-renders. The daily pass in
	// db/catalog-refresh.js refreshes catalogs in the background too.
	if ((!cached || isCatalogStale(cached)) && series.tvmaze_id) {
		getTvEpisodes(series.tvmaze_id)
			.then(async (data) => {
				if (!data) return;
				const saved = await saveCatalog(data);
				mount(view, await getTvSeriesById(id), saved);
			})
			.catch(() => {});
	}
}

async function mount(view, series, catalog) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front and do the async
	// work (group membership) WHILE THE OLD VIEW IS STILL ON SCREEN; only clear +
	// append + restore synchronously at the very end, so the browser never paints a
	// cleared/top state (that intermediate paint was the scroll jump, then flash).
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(series);

	// ---- feature hero ----
	const kickerBits = ["TV"];
	if (series.premiered_year) kickerBits.push(String(series.premiered_year));

	const figures = [{ value: series.rating != null ? series.rating : "—", label: "Rating" }];

	if (overriddenFields(series).length > 0) kickerBits.push("Edited");

	const hero = magDetailHero(series, {
		kicker: kickerBits.join(" · "),
		title: series.title,
		sticker: statusSticker(series.engagement_status, "tv"),
		figures,
		tag: (series.awards ?? [])[0] ? awardLabel(series.awards[0]) : null,
		onBack: () => navigateBack("/tv"),
		// "⋯": edit details / re-link / undo / reset (components/item-menu.js). The
		// catalog supplies season posters as cover candidates.
		onMenu: () =>
			openItemMenu({
				mediaType: "tv",
				record: series,
				catalog,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated, catalog),
			}),
	});

	// ---- fields ----
	const engagementField = magField(
		"Watch it?",
		magSelectRow(ENGAGEMENT_STATUSES, series.engagement_status, async (value) => {
			mount(view, await updateTvSeries(series.id, { engagement_status: value }), catalog);
		}, (v) => statusLabel(v, "tv")),
		{ index: 0 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(OWNERSHIP_STATUSES, series.ownership_status ?? "have_it", async (value) => {
			mount(view, await updateTvSeries(series.id, { ownership_status: value }), catalog);
		}, (v) => statusLabel(v, "tv")),
		{ index: 1 },
	);

	// Series rating — the default inherited by every season + episode unless
	// individually overridden.
	const ratingField = magField(
		"Rating (whole series)",
		magRating(series.rating ?? null, async (value) => {
			mount(view, await setSeriesRating(series.id, value), catalog);
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(series.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(AWARDS.map((a) => a.value), selectedAwards, async (next) => {
			mount(view, await setSeriesAwards(series.id, Array.from(next)), catalog);
		}, awardLabel),
		{ index: 4 },
	);

	const noteField = magField(
		"Notes",
		magNote(series.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "the early seasons are the best"',
			onSave: (v) => setSeriesNote(series.id, v),
		}),
		{ index: 5 },
	);

	const seasonsSection = renderSeasons(view, series, catalog);

	const groupsField = magField(
		"Groups",
		await groupsSection(series, groups, async () =>
			mount(view, await getTvSeriesById(series.id), catalog),
		),
		{ index: 1 },
	);

	const dates = magDateLine([
		["Last watched", formatDate(series.last_engaged_date)],
		["Added", formatDate(series.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${series.title}" from your list?`)) return;
		await deleteTvSeries(series.id);
		navigate("/tv");
		offerUndoDelete("tv", series, { reopenRoute: `/tv/${series.id}` });
	});

	clear(view);
	view.append(
		hero,
		magSummary(series.summary) || "",
		inTheNews(series),
		peopleRow("tv", series.tvmaze_id),
		engagementField,
		ownershipField,
		ratingField,
		awardsField,
		noteField,
		tagsField("tv", series, { suggestions: tagSuggestions("tv") }),
		reviewField("tv", series),
		progressField("tv", series),
		priceNoteField("tv", series),
		loanField("tv", series),
		startAgainButton("tv", series),
		seasonsSection,
		groupsField,
		dates,
		historySection(series),
		footer,
	);

	// Restore synchronously — in the same tick as the rebuild, so the browser never
	// paints the cleared/top state (a deferred rAF restore caused a visible flash).
	window.scrollTo(0, scrollY);
}

// The "Seasons" listing: a magazine section header over a stack of season rows.
// Each season becomes a magEpisodeRow whose number is the season number, kicker
// is the episode count + seen progress, and title is the season name. Tapping a
// row navigates to the same /tv/:id/season/:n route as before; a released season
// that isn't seen yet carries a trailing "Seen" button that marks the whole
// season watched (once seen, the button is gone — unsee from the season screen).
function renderSeasons(view, series, catalog) {
	const wrap = el("div", { style: { padding: "0 16px" } }, magSectionHeader("Seasons"));

	if (!catalog) {
		wrap.append(magLoading({ shape: "list" }));
		return wrap;
	}
	const seasons = (catalog.seasons ?? []).filter((s) => s.number != null);
	if (seasons.length === 0) {
		wrap.append(
			el("p", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "600", fontSize: "13px",
					color: MAG.dim, padding: "4px 2px",
				},
			}, "No season data found."),
		);
		return wrap;
	}

	const today = todayDate();
	const episodes = catalog.episodes ?? [];
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", paddingTop: "4px" } });
	for (const s of seasons) {
		const released = seasonReleased(s, episodes, today);
		const eps = episodes.filter((e) => e.season === s.number);

		// Child object for the textured thumb: inherit the series cover + tint.
		const child = {
			title: s.name || `Season ${s.number}`,
			cover_url: series.cover_url ?? null,
			media_type: "tv",
		};

		const seasonSeen = effectiveSeen(series, s.number, null, { released });

		let kicker;
		if (!released) {
			// Not aired yet — show when it airs instead of a seen/rating summary.
			const d = seasonPremiereDate(s, episodes);
			kicker = d ? `Airs ${formatReleaseDate(d)}` : "Not yet aired";
		} else {
			// An individual episode can still be unaired mid-season — don't count it
			// as seen by default.
			const seenCount = eps.filter((e) => {
				const epReleased = e.airdate ? e.airdate <= today : true;
				return effectiveSeen(series, s.number, e.number, { released: epReleased });
			}).length;
			const rating = effectiveRating(series, s.number, null, { released });
			const epLabel = eps.length
				? `${eps.length} episode${eps.length === 1 ? "" : "s"} · ${seenCount}/${eps.length} seen`
				: "No episodes";
			const kickerBits = [epLabel];
			if (rating != null) kickerBits.push(`★ ${rating}`);
			kicker = kickerBits.join("   ");
		}

		// A released, not-yet-seen season gets a "Seen" button that marks the whole
		// season watched; a seen or unaired season shows no trailing button.
		const showSeenBtn = released && !seasonSeen;
		list.append(
			magEpisodeRow(child, {
				number: s.number,
				kicker,
				onClick: () => navigate(`/tv/${series.id}/season/${s.number}`),
				hideAction: !showSeenBtn,
				addLabel: "Seen",
				onAdd: showSeenBtn
					? async () => mount(view, await setSeasonSeen(series.id, s.number, true), catalog)
					: undefined,
			}),
		);
	}
	wrap.append(list);
	return wrap;
}
